import { defineComponent as _defineComponent } from 'vue'
import { dir } from '@@/bits/current_dir'
import { WHITEBOARD_ATTACHMENT_PADDING } from '@@/bits/whiteboard_no_dependencies'
import OzBasePopover from '@@/library/v4/components/OzBasePopover.vue'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMediaPlayerStore } from '@@/pinia/surface_media_player'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import type { Post } from '@@/types'
import type BeethovenAttachmentPreview from '@@/vuecomponents/BeethovenAttachmentPreview.vue'
import SurfacePostAttachment from '@@/vuecomponents/SurfacePostAttachment.vue'
import SurfacePostAttachmentEmbedControl from '@@/vuecomponents/SurfacePostAttachmentEmbedControl.vue'
import SurfacePostPoll from '@@/vuecomponents/SurfacePostPoll.vue'
import WhiteboardAttachmentCornerActionButton from '@@/vuecomponents/WhiteboardAttachmentCornerActionButton.vue'
import { autoPlacement, offset } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { debounce, memoize } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { nextTick } from 'process'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'WhiteboardAttachment',
  props: {
    postId: null,
    width: null,
    height: null
  },
  emits: ["loaded"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'loaded'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  postId: number
  width: number
  height: number
};



// Data are set by parent component
const attachmentWidth = ref(props.width)

const componentRef = ref<typeof BeethovenAttachmentPreview | null>(null)
const $el = computed<HTMLElement | undefined>(() => componentRef.value?.$el)

function setWidthHeightAttrAndStyle(el: HTMLElement, dimensions: { width: number; height: number }): void {
  el.setAttribute('width', dimensions.width.toString())
  el.setAttribute('height', dimensions.height.toString())
  el.style.width = `${dimensions.width}px`
  el.style.height = `${dimensions.height}px`
}

const getImageAr = memoize(
  (imageThumbnailImageEl) =>
    parseFloat(imageThumbnailImageEl.getAttribute('width') ?? '1.0') /
    parseFloat(imageThumbnailImageEl.getAttribute('height') ?? '1.0'),
  (imageThumbnailImageEl) => imageThumbnailImageEl.src,
)
/**
 * Update the attachment size while resizing the component. This is done in our old Canvas when a post is resized.
 * Replicated the logic in resizable_mixin.js
 */
function updateAttachmentSizeWhileResizing(): void {
  const previewAspectRatio = parseFloat(
    $el.value?.querySelector('[data-aspect-ratio]')?.getAttribute('data-aspect-ratio') as string,
  )
  if (!previewAspectRatio) {
    return
  }
  const imageGridNumOfImages = parseInt(
    $el.value?.querySelector('.image-grid-container')?.getAttribute('data-image-grid-num-of-images') as string,
    10,
  )
  const usingImageGrid = imageGridNumOfImages > 0

  const postV2Dimensions = {
    width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
    height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / previewAspectRatio),
  }

  // $('.surface-post-attachment-inline-embed iframe', this.$el).attr(postV2Dimensions).css(postV2Dimensions)
  // $('.image-thumbnail-crop-frame,.image-thumbnail-image', this.$el).attr(postV2Dimensions).css(postV2Dimensions)

  const iframeEl = $el.value?.querySelector('.surface-post-attachment-inline-embed iframe') as
    | HTMLIFrameElement
    | undefined
  if (iframeEl) {
    setWidthHeightAttrAndStyle(iframeEl, postV2Dimensions)
  }

  if (usingImageGrid) {
    const imageGridAspectRatio = parseFloat(
      $el.value?.querySelector('.image-grid-container')?.getAttribute('data-image-grid-aspect-ratio') as string,
    )
    // Post View v2 with image grid: we only resize the grid container as the images in the grid always
    // expand to fill the container
    // When the grid has one image, we use that image's aspect ratio
    const ar = imageGridNumOfImages === 1 ? previewAspectRatio : imageGridAspectRatio
    const imageGridDimensions = {
      width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
      height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / ar),
    }

    // $('.image-grid-container', this.$el).attr(imageGridDimensions).css(imageGridDimensions)
    const imageGridContainerEl = $el.value?.querySelector('.image-grid-container') as HTMLElement | undefined
    if (imageGridContainerEl) {
      setWidthHeightAttrAndStyle(imageGridContainerEl, imageGridDimensions)
    }
  } else {
    const imageThumbnailCropFrameEl = $el.value?.querySelector('.image-thumbnail-crop-frame') as HTMLElement | undefined
    const imageThumbnailImageEl = $el.value?.querySelector('.image-thumbnail-image') as HTMLImageElement | undefined

    if (!imageThumbnailCropFrameEl && !imageThumbnailImageEl) {
      // console.warn('WTF: No image-thumbnail-crop-frame or image-thumbnail-image found in the attachment')
    }
    if (imageThumbnailCropFrameEl) {
      setWidthHeightAttrAndStyle(imageThumbnailCropFrameEl, postV2Dimensions)
    }
    if (imageThumbnailImageEl) {
      const imageDimensions = {
        width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
        height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / getImageAr(imageThumbnailImageEl)),
      }
      setWidthHeightAttrAndStyle(imageThumbnailImageEl, imageDimensions)
    }
  }
}

// Update beenhoven attachment width when finish resizing
// New logic: update to the largest width that the image has ever been resized to
// This is for when the image is resized to a smaller width, we don't fetch the image again from Beethoven
const largestImgWidthRendered = ref(props.width)
function updateAttachmentWidth(newWidth): void {
  attachmentWidth.value = newWidth
}
const debouncedUpdateAttachmentWidth = debounce(updateAttachmentWidth, 1000, { trailing: true })
watch(
  () => props.width,
  (newWidth) => {
    if (newWidth > largestImgWidthRendered.value) {
      largestImgWidthRendered.value = newWidth
      debouncedUpdateAttachmentWidth(largestImgWidthRendered.value)
    }
    updateAttachmentSizeWhileResizing()
  },
  { immediate: true, flush: 'post' },
)

const displayAttributes = ref<BeethovenDisplayAttributes | null>(null)
function handleImageLoaded(data: BeethovenDisplayAttributes): void {
  displayAttributes.value = data
  nextTick(() => {
    const img = $el.value?.querySelector('img') as HTMLImageElement & { __loadedHandled?: boolean }
    updateAttachmentSizeWhileResizing()
    // Here the img re-render. We need to update the attachment size again
    if (img && !img.__loadedHandled) {
      img.addEventListener('load', () => {
        emit('loaded')
        updateAttachmentSizeWhileResizing()
      })
    }
    img.__loadedHandled = true
  })
}
const surfacePostsStore = useSurfacePostsStore()
const post = computed<Post | undefined>(() => surfacePostsStore.postEntitiesById[props.postId])

function toggleEmbedControl(): void {
  // TODO
}

const xPoll = computed(() => post.value?.wish_content?.attachment_props?.type === 'poll')
const poll = computed(() => (xPoll.value ? post.value?.wish_content?.attachment_props : null))

const surfaceStore = useSurfaceStore()
const { user } = storeToRefs(surfaceStore)
const displayedPostColor = null

const surfaceMediaPlayerStore = useSurfaceMediaPlayerStore()
const expandedPostStore = useExpandedPostStore()
function expand(): void {
  if (post.value?.cid) {
    expandedPostStore.expandPost({ postCid: post.value.cid, xTextPanel: false })
  }
}
const isPostPlayingMedia = ref(false)

watch(
  () => surfaceMediaPlayerStore.currentlyPlayingPostCid,
  (newCurrentlyPlayingPostCid) => {
    if (newCurrentlyPlayingPostCid === post.value?.cid) {
      isPostPlayingMedia.value = surfaceMediaPlayerStore.isPostPlayingMedia(post.value?.cid)
      nextTick(() => {
        updateAttachmentSizeWhileResizing()
      })
    } else {
      isPostPlayingMedia.value = false
    }
  },
)

watch(
  () => useWhiteboardStore().currentFrameId,
  () => {
    componentRef.value?.postMessage('back-to-preview')
    surfaceMediaPlayerStore.unsetCurrentlyPlayingMedia()
  },
)

onMounted(() => {
  window.addEventListener('visibilitychange', updateAttachmentSizeWhileResizing)
})
onUnmounted(() => {
  window.removeEventListener('visibilitychange', updateAttachmentSizeWhileResizing)
  if (surfaceMediaPlayerStore.currentlyPlayingPostCid === post.value?.cid) {
    surfaceMediaPlayerStore.unsetCurrentlyPlayingMedia()
  }
})

return { __sfc: true,props, emit, attachmentWidth, componentRef, $el, setWidthHeightAttrAndStyle, getImageAr, updateAttachmentSizeWhileResizing, largestImgWidthRendered, updateAttachmentWidth, debouncedUpdateAttachmentWidth, displayAttributes, handleImageLoaded, surfacePostsStore, post, toggleEmbedControl, xPoll, poll, surfaceStore, user, displayedPostColor, surfaceMediaPlayerStore, expandedPostStore, expand, isPostPlayingMedia, dir, WHITEBOARD_ATTACHMENT_PADDING, OzBasePopover, SurfacePostAttachment, SurfacePostAttachmentEmbedControl, SurfacePostPoll, WhiteboardAttachmentCornerActionButton, autoPlacement, offset, Portal }
}

})